import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
    C10sOrderListParams, C10sOrderRow, C10sOrderStats, C10sOrderStatus, C10sSite,
    getC10sOrder, getC10sOrderStats, listC10sOrders,
} from '../../api/assets';
import { ApiError } from '../../api/request';
import Modal from '../../components/Modal';
import Pagination from '../../components/Pagination';
import { fmtUnix, MerchantBadge, ParamTable, paramTypeText, processTechText } from './c10sLabels';
import { StatCard } from './parts';
import { ProductImg } from './productLabels';
import { thumbUrl, THUMB_W_SM } from '../../utils/imgUrl';

// 商家数据中心「订单」tab (2026-09-15 需求: 订单单独成 tab, 商品列表与详情都能跳过来):
// 一格 = 一条订单明细 (源侧 t_order_item), 图是买家定制后的设计图.
//
// 跨商品视图: 不带 productId 就是全站订单, 带了就是"这个商品的订单" —— 商品侧入口跳过来即此,
// 页面顶上给一条可清除的筛选说明, 不另做一套页面.
//
// 量级决定的取舍 (美服生产 163,930 条明细, 头部单个商品 12,829 条):
//   - 页码分页 (与商品 tab 同一个 Pagination), 但**顺序往后翻时带上一页给的 nextBefore 锚点**
//     —— 后端命中锚点就走主键, 不吃 OFFSET; 手动跳页没有锚点, 退回 OFFSET (后端两条路都支持);
//   - 列表只带各面设计图与设备/材质, **下单参数快照点开某一单时才拉** (单条 product_sides 可达数百 KB).
//
// 图源一律是面上的 designFileImageUrl (后端已去重成 images) —— 源侧顶层的 design_file_image_url
// 与缩略图字段实测全空.

/** 加工状态标签 (源侧 process_status 只有这两个值) */
const STATUS_LABEL: Record<C10sOrderStatus, string> = { pending: '待加工', completed: '已加工' };

function StatusBadge({ status }: { status: C10sOrderStatus }) {
    const done = status === 'completed';
    return (
        <span className={`px-1.5 py-0.5 rounded text-[10px] ${done ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>
            {STATUS_LABEL[status] || status}
        </span>
    );
}

/**
 * 一格订单: 设计图为主, 下面三行信息 (状态·数量·单价 / SKU / 商家·商品) + 下单日期.
 *
 * 排版口径 (2026-09-16 用户): 一行 7 个 × 5 行, 所以每格窄 —— 图给足 (占满格宽, 1:1),
 * 文字分行留白, 不把六七个字段挤成一坨; 时间只留日期 (时分在详情里看), 数量 1 不显示.
 *
 * 格子整体点开订单详情, 商品标题那一行**单独点跳商品详情** —— 所以外层是 div 而不是 button
 * (HTML 不允许 button 里再套 button), 键盘可达性用 role + tabIndex + Enter/Space 补上.
 */
function OrderCell({ o, active, showProduct, onPick, onOpenProduct }: {
    o: C10sOrderRow; active: boolean; showProduct: boolean; onPick: () => void; onOpenProduct: () => void;
}) {
    const cover = o.images[0] || '';
    return (
        <div role="button" tabIndex={0} onClick={onPick}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPick(); } }}
            title="点击看这一单的设计图与下单参数快照"
            className={`group text-left rounded-2xl border bg-white overflow-hidden cursor-pointer transition
                ${active ? 'border-blue-400 ring-2 ring-blue-100' : 'border-slate-100 hover:border-slate-300 hover:shadow-md'}`}>
            {/* 图区: 浅灰底衬托, 占满格宽 —— 设计图是这一屏真正要看的东西 */}
            <div className="relative bg-slate-50/70 border-b border-slate-100" style={{ aspectRatio: '1 / 1' }}>
                {cover ? (
                    <ProductImg src={cover} className="w-full h-full object-contain p-1.5" title={o.sku} />
                ) : (
                    <div className="w-full h-full flex items-center justify-center text-[11px] text-slate-300">无设计图</div>
                )}
                {o.images.length > 1 && (
                    <span className="absolute top-1.5 right-1.5 rounded-md bg-slate-900/60 px-1.5 py-0.5 text-[10px] text-white"
                        title={`${o.images.length} 个面各有设计图`}>+{o.images.length - 1}</span>
                )}
                <div className="absolute top-1.5 left-1.5"><StatusBadge status={o.processStatus} /></div>
            </div>

            <div className="px-2.5 py-2 space-y-1.5">
                <div className="flex items-baseline gap-1.5">
                    <span className="font-mono text-[11px] text-slate-700 truncate" title={o.sku}>{o.sku || '(无 SKU)'}</span>
                    {o.price > 0 && <span className="ml-auto text-[11px] font-semibold text-slate-800 tabular-nums">${o.price}</span>}
                </div>
                <div className="flex items-center gap-1.5 text-[11px] text-slate-400">
                    <span>{fmtUnix(o.createdAt).slice(0, 10)}</span>
                    {o.quantity > 1 && (
                        <span className="ml-auto rounded bg-slate-100 px-1.5 text-slate-600 tabular-nums" title="下单数量">×{o.quantity}</span>
                    )}
                </div>
                {showProduct && (
                    <div className="flex items-center gap-1.5 min-w-0 pt-1 border-t border-slate-50">
                        {o.merchant ? (
                            <>
                                <MerchantBadge kind={o.merchant.kind} />
                                <button type="button"
                                    className="truncate text-left text-[11px] text-slate-500 hover:text-blue-600 hover:underline"
                                    title={`${o.curProductTitle || o.productTitle}｜商家 ${o.merchant.storeName || '—'}\n点击查看商品详情`}
                                    onClick={(e) => { e.stopPropagation(); onOpenProduct(); }}>
                                    {o.curProductTitle || o.productTitle || '—'}
                                </button>
                            </>
                        ) : (
                            // 商品补不到 (第三方渠道商品 / 商品已删): 不给可点的空标题, 跳过去也是 404.
                            // 下单时的标题快照还在, 拿它兜底说明这单买的是什么.
                            <span className="truncate text-[11px] text-slate-400"
                                title={`商品已下架或不是定制渠道商品，无法打开详情\n下单时的标题：${o.productTitle || '—'}`}>
                                {o.productTitle || '商品已不可用'}
                            </span>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

/** 点开的那一单: 逐面的设计图 + 下单时的加工参数快照 */
function OrderPanel({ id, site, onZoom, onOpenProduct }: {
    id: string; site: C10sSite; onZoom: (src: string) => void;
    /** 跳这一单买的那个商品 (商品已被删时后端查不到明细, 所以这里拿到行就一定有 productId) */
    onOpenProduct: (productId: string) => void;
}) {
    const [row, setRow] = useState<C10sOrderRow | null>(null);
    const [error, setError] = useState('');

    useEffect(() => {
        let alive = true;
        setRow(null);
        setError('');
        getC10sOrder(id, site).then((r) => { if (alive) setRow(r); })
            .catch((e) => { if (alive) setError(e instanceof ApiError ? e.message : String(e)); });
        return () => { alive = false; };
    }, [id, site]);

    if (error) return <div className="text-xs text-rose-600">错误: {error}</div>;
    if (!row) return <div className="text-xs text-slate-400">加载中…</div>;

    return (
        <div className="space-y-3.5">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                <StatusBadge status={row.processStatus} />
                <span className="font-mono text-slate-700">{row.sku || '(无 SKU)'}</span>
                <span>数量 {row.quantity}</span>
                {row.price > 0 && <span>单价 ${row.price}</span>}
                <span>{processTechText(row.processTech)}</span>
                {row.deviceCode && <span>设备 {row.deviceCode}</span>}
                <span>下单 {fmtUnix(row.createdAt)}</span>
                {row.etsyTransactionId && <span title="Etsy 外站订单号">Etsy #{row.etsyTransactionId}</span>}
                <span className="ml-auto font-mono text-slate-400" title="订单 id / 明细 id">
                    单 {row.orderId} · 行 {row.id}
                </span>
            </div>
            <div className="flex flex-wrap items-center gap-x-2 text-[11px] text-slate-400">
                <span>下单时的商品标题快照：{row.productTitle || '—'}</span>
                {row.productVariantId && <span className="font-mono">规格 #{row.productVariantId}</span>}
                <button type="button" className="text-blue-600 hover:underline"
                    title="打开这一单买的那个商品的详情页"
                    onClick={() => onOpenProduct(row.productId)}>查看商品详情 →</button>
            </div>

            {row.sides.length === 0 ? (
                <div className="text-[11px] text-slate-400">该明细没有面数据（源侧 product_sides 为空）</div>
            ) : row.sides.map((s, i) => (
                <div key={s.sideId || i} className="rounded-xl bg-slate-50/70 p-3.5 space-y-2.5">
                    <div className="flex items-start gap-3">
                        {s.designFileImageUrl ? (
                            <ProductImg src={s.designFileImageUrl} className="rounded-xl object-contain bg-white ring-1 ring-slate-100 cursor-zoom-in shrink-0"
                                style={{ width: 96, height: 96 }} onClick={() => onZoom(s.designFileImageUrl)} />
                        ) : (
                            <div className="rounded-xl bg-slate-100 text-[11px] text-slate-400 flex items-center justify-center shrink-0"
                                style={{ width: 96, height: 96 }}>无图</div>
                        )}
                        <div className="min-w-0 flex-1 text-[11px] text-slate-500 space-y-1">
                            <div className="font-semibold text-slate-600">面 {i + 1}{s.sideName ? ` · ${s.sideName}` : ''}</div>
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                                {s.machine && <span>设备 {s.machine}</span>}
                                {s.processTech && <span>{processTechText(s.processTech)}</span>}
                                {s.processingMode && <span className="font-mono">{s.processingMode}</span>}
                                {s.designColor && (
                                    <span className="inline-flex items-center gap-1">
                                        颜色 <i className="w-2.5 h-2.5 rounded-sm ring-1 ring-slate-200" style={{ background: s.designColor }} />
                                        <span className="font-mono">{s.designColor}</span>
                                    </span>
                                )}
                                {s.texture && (
                                    <span className="inline-flex items-center gap-1">
                                        材质
                                        {s.texture.image && <img src={thumbUrl(s.texture.image, THUMB_W_SM)} alt="" className="w-3.5 h-3.5 rounded-sm object-cover" loading="lazy" decoding="async" />}
                                        {s.texture.title || `#${s.texture.id}`}
                                    </span>
                                )}
                                {s.processingPosition && (
                                    <span title="源侧 processingPosition">
                                        定位 ({s.processingPosition.x ?? 0}, {s.processingPosition.y ?? 0}, {s.processingPosition.z ?? 0})
                                    </span>
                                )}
                            </div>
                            <div className="flex flex-wrap gap-x-3">
                                {s.designElementImageUrl && (
                                    <a href={s.designElementImageUrl} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">设计元素图 ↗</a>
                                )}
                                {s.designElementImageSvgUrl && (
                                    <a href={s.designElementImageSvgUrl} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">元素 SVG ↗</a>
                                )}
                            </div>
                        </div>
                    </div>
                    <ParamTable parameters={s.parameters || []}
                        empty="这一单没有加工参数快照（源侧写的是全零占位，已过滤）" />
                    {s.parameters && s.parameters.length > 0 && (
                        <div className="text-[10px] text-slate-400">
                            下单快照 · {s.parameters.map((p) => paramTypeText(p.type)).join(' / ')}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}

/** 小筛选按钮组 (加工状态) */
function Toggle<T extends string>({ label, value, options, onChange }: {
    label: string; value: T; options: { val: T; label: string }[]; onChange: (v: T) => void;
}) {
    return (
        <div className="flex items-center gap-1.5">
            <span className="text-[11px] text-slate-400">{label}</span>
            <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden">
                {options.map((o) => (
                    <button key={o.val} type="button" onClick={() => onChange(o.val)}
                        className={`px-2 py-1 text-[11px] ${value === o.val ? 'bg-slate-700 text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}>
                        {o.label}
                    </button>
                ))}
            </div>
        </div>
    );
}

// 一页 35 条 = 一行 7 个 × 5 行 (2026-09-16 用户定的排版). 35 同时能被窄屏的 5 列整除,
// 所以两个断点都正好排满整行 —— 用 auto-fill 时列数随窗口宽度浮动, 最后一行常剩一两格
// 孤零零贴在分页条上面 (这就是"分页多出来一个"的由来).
const PAGE_SIZE = 35;

/**
 * 订单 tab 本体。
 * productId 由商品侧入口带来 (URL ?productId=)，为空即全站订单；
 * productFilter 是商品/商家侧的筛选 (与商品 tab 共用同一组状态，切 tab 不丢)。
 */
function C10sOrders({ site, productId, productLabel, productFilter, onClearProduct, onZoom }: {
    site: C10sSite;
    productId?: string;
    /** 收窄到单个商品时顶上显示什么 (商品标题，没有就显示 id) */
    productLabel?: string;
    productFilter?: Pick<C10sOrderListParams, 'keyword' | 'material' | 'textureId' | 'merchantKind' | 'enterpriseId'>;
    onClearProduct?: () => void;
    onZoom: (src: string) => void;
}) {
    const [stats, setStats] = useState<C10sOrderStats | null>(null);
    const [rows, setRows] = useState<C10sOrderRow[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    // anchors[n] = 第 n 页的 before 锚点 (由第 n-1 页的响应给出); 只有顺序翻页才有, 跳页时为空.
    const [anchors, setAnchors] = useState<Record<number, string>>({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [status, setStatus] = useState<C10sOrderStatus | ''>('');
    const [picked, setPicked] = useState<string | null>(null);
    const history = useHistory();
    // 订单 → 商品详情 (站点跟着带, 详情页要按它决定查哪个库).
    const openProduct = useCallback((pid: string) => {
        history.push(`/assets/c10s-products/${pid}${site === 'us' ? '' : `?site=${site}`}`);
    }, [history, site]);

    // 商品/商家侧筛选来自外层 (与商品 tab 同一组状态); 展开成稳定的依赖值, 免得每次渲染都重查.
    const { keyword = '', material = '', textureId = '', merchantKind = '', enterpriseId = '' } = productFilter || {};
    const query: C10sOrderListParams = {
        site, productId: productId || undefined, status: status || undefined,
        keyword: keyword || undefined, material: material || undefined, textureId: textureId || undefined,
        merchantKind: merchantKind || undefined, enterpriseId: enterpriseId || undefined,
    };
    const dep = JSON.stringify(query);

    // 统计条与列表是两次请求 (统计不受分页影响).
    useEffect(() => {
        let alive = true;
        setStats(null);
        getC10sOrderStats(query)
            .then((r) => { if (alive) setStats(r); })
            .catch((e) => { if (alive) setError(e instanceof ApiError ? e.message : String(e)); });
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dep]);

    // 换筛选回到第 1 页并丢掉锚点 (锚点是跟着筛选走的, 换了筛选原来的位置没有意义).
    useEffect(() => {
        setPage(1);
        setAnchors({});
    }, [dep]);

    useEffect(() => {
        let alive = true;
        setRows([]);
        setPicked(null);
        setLoading(true);
        setError('');
        listC10sOrders({ ...query, page, pageSize: PAGE_SIZE, before: anchors[page] })
            .then((r) => {
                if (!alive) return;
                setRows(r.list);
                setTotal(r.total);
                // 记下下一页的锚点: 顺序往后翻时后端就能走主键 seek.
                if (r.nextBefore) {
                    setAnchors((prev) => (prev[page + 1] === r.nextBefore ? prev : { ...prev, [page + 1]: r.nextBefore as string }));
                }
            })
            .catch((e) => { if (alive) setError(e instanceof ApiError ? e.message : String(e)); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dep, page]);

    const filteredByProduct = Boolean(productId);
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    return (
        <div className="space-y-4">
            {error && <div className="card p-3 text-sm text-rose-600 border-rose-200 bg-rose-50">错误: {error}</div>}

            {/* 收窄到单个商品时说清楚"在看谁的订单", 并给一键看全站 */}
            {filteredByProduct && (
                <div className="card px-4 py-2.5 flex flex-wrap items-center gap-2 text-xs bg-blue-50/40 border-blue-100">
                    <span className="text-slate-500">筛选：商品</span>
                    <span className="font-medium text-slate-800">{productLabel || `#${productId}`}</span>
                    <span className="font-mono text-[11px] text-slate-400">#{productId}</span>
                    <span className="text-slate-400">·</span>
                    <span className="text-slate-500">{total.toLocaleString()} 条订单明细</span>
                    {onClearProduct && (
                        <button type="button" className="ml-auto text-blue-600 hover:underline" onClick={onClearProduct}>
                            清除商品筛选，看全部订单
                        </button>
                    )}
                </div>
            )}

            <div className="grid grid-cols-5 gap-4">
                <StatCard label="订单数" value={stats ? stats.orders.toLocaleString() : '—'} sub="按订单去重" />
                <StatCard label="明细条数" value={stats ? stats.items.toLocaleString() : '—'} sub="一行商品算一条" />
                <StatCard label="件数" value={stats ? stats.quantity.toLocaleString() : '—'} sub="下单数量合计" />
                <StatCard label="已加工" value={stats ? stats.completed.toLocaleString() : '—'} sub="process_status=completed" accent="text-teal-600" />
                <StatCard label="待加工" value={stats ? stats.pending.toLocaleString() : '—'} sub="process_status=pending" accent="text-amber-600" />
            </div>

            {/* 「设计图 有/无」筛选已下线 (2026-09-17): 源侧没有可用索引, 判定要靠
                JSON_SEARCH 全表扫 product_sides (单行数百 KB), count 实测美服 8.9s / 国服 6.0s,
                冷缓存时整页要等几十秒; 而它几乎筛不掉东西 —— 有图占比美服 94.1%、国服 99.98%。
                接口仍支持 hasImage 参数 (OpenAPI/SDK 契约不动), 只是页面不再暴露。 */}
            <div className="card px-4 py-3 flex flex-wrap items-center gap-x-5 gap-y-2">
                <Toggle<C10sOrderStatus | ''> label="加工状态" value={status} onChange={setStatus}
                    options={[{ val: '', label: '全部' }, { val: 'pending', label: '待加工' }, { val: 'completed', label: '已加工' }]} />
                <span className="text-[11px] text-slate-400">
                    首末单 {stats ? `${fmtUnix(stats.firstAt)} ~ ${fmtUnix(stats.lastAt)}` : '—'}
                </span>
                <span className="ml-auto text-[11px] text-slate-400">第 {page} / {totalPages} 页</span>
            </div>

            <div className="card">
                <div className="flex items-baseline px-5 pt-4 pb-3">
                    <div className="text-sm font-semibold text-slate-700">订单明细</div>
                    <div className="ml-2.5 text-xs text-slate-400">
                        图是买家定制后的设计图 · 按下单时间倒序 · 点一格看下单时的加工参数快照
                    </div>
                    <div className="ml-auto text-xs text-slate-400">共 {total.toLocaleString()} 条</div>
                </div>
                {rows.length === 0 ? (
                    <div className="text-center text-slate-400 py-12 text-sm">{loading ? '加载中…' : '当前筛选下没有订单明细'}</div>
                ) : (
                    <div className="grid grid-cols-5 lg:grid-cols-7 gap-3 px-5 pb-5">
                        {rows.map((o) => (
                            <OrderCell key={o.id} o={o} active={picked === o.id} showProduct={!filteredByProduct}
                                onPick={() => setPicked(o.id)}
                                onOpenProduct={() => openProduct(o.productId)} />
                        ))}
                    </div>
                )}
                {totalPages > 1 && (
                    <div className="px-5 py-3.5 border-t border-slate-100 flex justify-end">
                        <Pagination page={page} totalPages={totalPages} onChange={setPage} total={total} />
                    </div>
                )}
            </div>

            {/* 详情走模态框而不是往页面底部追加面板 —— 图墙一屏几十格, 追加的面板在视口外, 点了像没反应 */}
            <Modal open={Boolean(picked)} onClose={() => setPicked(null)} width={860}
                title="订单明细 · 下单快照"
                subtitle="逐面的买家设计图与下单时写进明细的加工参数（源侧全零占位已过滤）">
                {picked && <OrderPanel id={picked} site={site} onZoom={onZoom} onOpenProduct={openProduct} />}
            </Modal>
        </div>
    );
}

export default C10sOrders;
